import './productAdditionalInformation.scss';

import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, AlertStatus, Button, Modal, Size } from '@eliberty/design-system-react';

import { TextHTML } from '~atoms';
import { ResponsiveSize } from '~constants';
import { findModalPortal } from '~helpers';
import { useMediaQuery } from '~hooks';
import { Maybe } from '~types/appsync.types';

interface ProductAdditionalInformationProps {
  includedOptions?: string[];
  shortDescription?: Maybe<string>;
}

const ProductAdditionalInformation = ({
  shortDescription,
  includedOptions = [],
}: ProductAdditionalInformationProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const intl = useIntl();
  const isMobile = useMediaQuery(ResponsiveSize.SCREEN_S_MOBILE);

  const hasIncludedOptions = includedOptions.length > 0;

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (!shortDescription && includedOptions.length === 0) {
    return null;
  }

  return (
    <>
      <Alert
        className='ProductAdditionalInformation'
        status={AlertStatus.Info}
        title={intl.formatMessage({
          defaultMessage: 'Check special information for this duration!',
          id: 'checkSpecialInfo',
        })}
        action={intl.formatMessage({ defaultMessage: 'View details', id: 'viewDetails' })}
        onAction={() => setIsModalOpen(true)}
      />

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={intl.formatMessage({ defaultMessage: 'Special Information', id: 'specialInfo' })}
          portalProps={{ target: findModalPortal() }}
          centered
          size={Size.L}
        >
          <Modal.Body>
            {shortDescription && <TextHTML text={shortDescription} />}
            {hasIncludedOptions && (
              <div className='ProductAdditionalInformation_includedAdvantages'>
                <div className='ProductAdditionalInformation_includedAdvantagesTitle'>
                  <FormattedMessage
                    id='includedAdvantages'
                    defaultMessage='Advantages included in this offer'
                  />
                </div>
                <ul className='ProductAdditionalInformation_includedAdvantagesList'>
                  {includedOptions.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button size={Size.M} onClick={handleCloseModal} fullWidth={isMobile}>
              <FormattedMessage defaultMessage='Close' id='close' />
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ProductAdditionalInformation;
